import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild} from '@angular/core';
import {DrawingImageUploadHelper} from '@application/helper/drawing/drawing-image-upload-helper';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {ConflictType} from '@domain/conflicts/conflict-type';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfiguration} from '@domain/production-schedule/drawing-configuration';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {DrawingRecolorInformation} from '@domain/production-schedule/drawing-recolor-information';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {EpDrawing} from '@domain/production-schedule/ep-drawing';
import {Permission} from '@domain/profile/permission.enum';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {CadCamService} from '@infrastructure/cad-cam/cad-cam.service';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {PROFILE, Profile} from '@infrastructure/http/profile/profile';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {OverviewListArticleForOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-article-for-order-line';
import {
  AlertDialogResult,
  AssertionUtils,
  BaseComponent,
  Conflict,
  ConflictsDialogComponent,
  ConflictsDialogData,
  DesignFileType,
  DialogBuilderFactoryService,
  DialogType,
  NavigationHelperPrototypeService,
  ObjectUtils,
  ToastService,
  TranslateService
} from '@vdw/angular-component-library';
import {cloneDeep, forEach, isEmpty, isEqual, isNil, isUndefined, size} from 'lodash-es';
import {Observable, Subscription} from 'rxjs';
import {finalize, map, retry, switchMap, takeUntil} from 'rxjs/operators';
import {DrawingConfigurationForEpDrawing} from '../drawing-configuration-for-ep-drawing';
import {EditDrawingFormComponent} from '../drawing-form/edit/edit-drawing-form.component';
import {DrawingConfigurationForm} from './drawing-configuration-form';
import {NavigationDrawingData} from './navigation-drawing-data.interface';
import {NavigationSaveAsEpData} from './save-as-ep/navigation-save-as-ep-data.interface';

@Component({
  selector: 'app-drawing-library-details',
  templateUrl: './drawing-library-details.component.html',
  styleUrls: ['./drawing-library-details.component.scss']
})
export class DrawingLibraryDetailsComponent extends BaseComponent implements OnChanges, OnInit {
  private static readonly designTranslationKey = 'DESIGN_LIBRARY.DESIGN';
  @Input() public drawingId: number;
  @Input() public drawingState: Drawing;
  @Input() public canShowFooter: boolean = true;
  @Input() public disableAllFields: boolean = false;
  @Input() public scrollToConfiguration: boolean = false;
  @Input() public productionScheduleDrawing: OverviewListArticleForOrderLine | ListDrawingConfiguration;
  @Output() public drawingDeleted: EventEmitter<Drawing[]> = new EventEmitter<Drawing[]>();
  @Output() public drawingCanceled: EventEmitter<void> = new EventEmitter<void>();
  @Output() public drawingUpdated: EventEmitter<Drawing> = new EventEmitter<Drawing>();
  @Output() public drawingReplaced: EventEmitter<DrawingImage[]> = new EventEmitter<DrawingImage[]>();
  @ViewChild('downloadAnchor') public downloadAnchor: ElementRef;
  @ViewChild('drawingImageReplaceImageInput') public drawingImageReplaceImageInput: ElementRef;
  public drawingShapes: string[] = [];
  public drawing: Drawing;
  public drawingConfigurationForEpDrawing: DrawingConfigurationForEpDrawing = null;
  public texeditWritePermission: Permission = Permission.TEXEDIT_EDIT;
  public openExternalPermission: Permission = Permission.TEXEDIT_VIEW_EP;
  public drawingFormComponent: EditDrawingFormComponent;
  public saveAsEpForm: DrawingConfigurationForm;
  private loadingDrawing = false;
  private subscriptionForGettingDrawing: Subscription;
  private savingAsEp: boolean;
  private readonly updateDrawingTranslationKey: string = 'GENERAL.ACTIONS.UPDATED_OBJECT';
  public readonly allowedFileExtensionForFiles = [DesignFileType.BMP, DesignFileType.PVD].join();

  public constructor(
    @Inject(DRAWINGS) private readonly drawings: Drawings,
    @Inject(PROFILE) private readonly profile: Profile,
    private readonly toastService: ToastService,
    private readonly renderer: Renderer2,
    private readonly cadCamService: CadCamService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly drawingImageUploadHelper: DrawingImageUploadHelper,
    private readonly navigationHelperService: NavigationHelperService<NavigationDrawingData & NavigationSaveAsEpData>,
    private readonly navigationHelperPrototypeService: NavigationHelperPrototypeService
  ) {
    super();

    this.drawingImageUploadHelper.unSubscribeOnViewDestroy = this.unSubscribeOnViewDestroy;
  }

  @ViewChild(EditDrawingFormComponent) public set content(content: EditDrawingFormComponent) {
    this.drawingFormComponent = content;
    this.changeDetectorRef.detectChanges();
  }

  public ngOnInit(): void {
    if (!isNil(this.drawingId) && isNil(this.drawing)) {
      this.getDrawing();
      this.getDrawingShapes();
      this.setSavingAsEp();
    } else if (isNil(this.drawingId)) {
      this.drawing = null;
      this.savingAsEp = false;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!isNil(this.drawingId) && (isNil(this.drawing) || !isEqual(this.drawingId, changes.drawingId.previousValue))) {
      this.getDrawing();
      this.getDrawingShapes();
      this.setSavingAsEp();
    } else if (isNil(this.drawingId)) {
      this.drawing = null;
      this.savingAsEp = false;
    }
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<NavigationDrawingData>({drawing: this.drawingFormComponent?.createDrawingFromForm() ?? this.drawing});
  }

  public onDelete(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: this.drawing.name}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((result: AlertDialogResult) => {
        if (isEqual(result, AlertDialogResult.CONFIRM)) {
          this.deleteDrawing();
        }
      });
  }

  public onDownload(): void {
    if (!isEmpty(this.downloadAnchor.nativeElement.href)) {
      this.downloadAnchor.nativeElement.click();
    } else {
      this.drawings
        .downloadFile(this.drawing.image.id)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((file: ArrayBuffer) => {
          const blob = new Blob([file]);
          this.renderer.setAttribute(this.downloadAnchor.nativeElement, 'href', URL.createObjectURL(blob));
          this.renderer.setAttribute(this.downloadAnchor.nativeElement, 'download', `${this.drawing.name}.ep`);
          this.downloadAnchor.nativeElement.click();
        });
    }
  }

  public onOpenCadCam(target: HTMLElement): void {
    this.cadCamService.openCadCam(target, this.getExternalApplicationLink(), this.unSubscribeOnViewDestroy);
  }

  public canUpdateDrawing(): boolean {
    return !isNil(this.drawingFormComponent) && this.drawingFormComponent.isValid();
  }

  public updateDrawing(): void {
    this.saving = true;
    this.drawing = this.drawingFormComponent.createDrawingFromForm();
    this.drawings
      .update(this.drawing)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe(() => {
        this.toastService.showInfo({
          tapToDismiss: false,
          timeOut: 2000,
          message: this.translate.instant(this.updateDrawingTranslationKey, {
            object: this.translate.instant(DrawingLibraryDetailsComponent.designTranslationKey, {count: 1}),
            name: this.drawing.name
          })
        });

        this.drawingUpdated.emit(this.drawing);
      });
  }

  public cancel(): void {
    if (this.navigationHelperPrototypeService.contentSwitcherDialogActive()) {
      this.navigationHelperPrototypeService.navigateBack();
    } else {
      this.drawingCanceled.emit();
    }
  }

  public showLoadingView(): void {
    this.loadingDrawing = true;
  }

  public hideLoadingView(): void {
    this.loadingDrawing = false;
  }

  public canShowLoaderForDrawing(): boolean {
    return this.loadingDrawing;
  }

  public canShowLabelForNoDrawingSelected(): boolean {
    return isNil(this.drawing) && !this.canShowLoaderForDrawing();
  }

  public canDisplayDetails(): boolean {
    return !isNil(this.drawing) && !this.canShowLoaderForDrawing();
  }

  public canShowBMPToolbars(): boolean {
    return this.canDisplayDetails() && this.isDrawingTypeBmp() && this.canShowFooter;
  }

  public canShowOtherToolbars(): boolean {
    return this.canDisplayDetails() && !this.isDrawingTypeBmp() && this.canShowFooter;
  }

  public isDrawingTypeEp(): boolean {
    return this.drawing.drawingType === DrawingType.EP;
  }

  public changeDrawingImage(image: DrawingImage): void {
    this.drawing.image = image;
    this.drawing.missingDesign = false;
    this.drawing.dimensionsInPixels.widthInPx = image.dimensionsInPx?.widthInPx ?? 1;
    this.drawing.dimensionsInPixels.heightInPx = image.dimensionsInPx?.heightInPx ?? 1;
    const configurations: DrawingConfiguration[] = [];
    configurations.push(...this.drawing.configurations);
    this.drawing.configurations.splice(0, size(this.drawing.configurations));
    forEach(configurations, (configuration: DrawingConfigurationWithColorAndYarnSet) => {
      this.drawings
        .getRecoloredImageConfiguration(this.drawing.image.id, configuration.quality.id, configuration.colorSet.id, null, null, TargetForListOfDrawingsEnum.DRAWING_LIBRARY, {
          input: image.dimensionsInPx
        })
        .pipe(retry(2))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((drawingRecolorInformation: DrawingRecolorInformation) => {
          const newConfiguration = new DrawingConfigurationWithColorAndYarnSet(
            {id: configuration.image.id, data: drawingRecolorInformation.image},
            drawingRecolorInformation.mainColorsCreelPosition,
            drawingRecolorInformation.borderColorsCreelPosition,
            drawingRecolorInformation.colorSet,
            configuration.quality,
            drawingRecolorInformation.commercialDimensionsInMm,
            null
          );

          this.drawing.configurations.push(newConfiguration);
          this.drawingFormComponent.changeDrawingImage(image);

          if (isEqual(size(this.drawing.configurations), size(configurations))) {
            this.hideLoadingView();
          }
        });
    });
  }

  public onDrawingReplaceImageFileChange(files: File[]): void {
    this.showLoadingView();
    this.drawings.clearUploadQueue();

    this.drawingImageUploadHelper.uploadFilesWithCustomResultAction(files, [DrawingType.BMP, DrawingType.PVD], (image: DrawingImage, localFileCopy: File[]) => {
      if (isNil(image)) {
        this.hideLoadingView();
        return;
      }

      this.drawings.addImageToUploadQueue(image);
      this.drawingImageReplaceImageInput.nativeElement.value = '';

      if (this.drawings.getUploadQueue()?.length === localFileCopy?.length) {
        this.drawings
          .uploadDrawingImages(this.drawings.getUploadQueue())
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe((drawingImages: DrawingImage[]) => {
            this.drawingReplaced.emit(drawingImages);
          });
      }
    });
  }

  public canShowReplaceImageButton(): boolean {
    return !isNil(this.drawing) && this.isDrawingTypeBmp() && (!this.isDrawingBeingUsed() || this.drawing.missingDesign);
  }

  public onEpSaved(): void {
    this.closeSaveAsEp();
  }

  public openSaveAsEp(drawingConfigurationForEpDrawing: DrawingConfigurationForEpDrawing, hasSaveAsEpState: boolean = false): void {
    if (!hasSaveAsEpState) {
      this.saveAsEpForm = null;
    }

    if (this.drawingFormComponent) {
      this.drawing = this.drawingFormComponent.createDrawingFromForm();
    }

    this.drawingConfigurationForEpDrawing = isNil(drawingConfigurationForEpDrawing)
      ? {
          image: cloneDeep(this.drawing.image)
        }
      : drawingConfigurationForEpDrawing;
    this.savingAsEp = true;
  }

  public closeSaveAsEp(): void {
    this.savingAsEp = false;
    this.drawingConfigurationForEpDrawing = {};
  }

  public canShowSaveAsEp(): boolean {
    return this.savingAsEp;
  }

  public isDrawingBeingUsed(): boolean {
    return !isUndefined(this.drawing) && this.drawing.used;
  }

  public openConflictsDialog(): void {
    this.drawings
      .getConflicts(this.drawing.id)
      .pipe(
        switchMap((conflicts: Conflict[]) => {
          return this.dialogBuilderFactoryService
            .getBuilder()
            .withClass('alert-dialog')
            .openDialog(ConflictsDialogComponent, ConflictsDialogData.createInUseData(DrawingLibraryDetailsComponent.designTranslationKey, this.drawing.name, conflicts, ConflictType));
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe();
  }

  private isDrawingTypeBmp(): boolean {
    return this.drawing.drawingType === DrawingType.BMP;
  }

  private deleteDrawing(): void {
    this.drawings
      .delete(this.drawing.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.toastService.showInfo({
          tapToDismiss: false,
          timeOut: 2000,
          message: this.translate.instant('GENERAL.ACTIONS.DELETED_OBJECT', {object: this.translate.instant(DrawingLibraryDetailsComponent.designTranslationKey, {count: 1})})
        });

        this.drawingDeleted.emit([this.drawing]);
      });
  }

  private getDrawing(): void {
    if (!AssertionUtils.isNullOrUndefined(this.drawingState)) {
      this.drawing = this.drawingState;
      return;
    }

    this.loadingDrawing = true;

    if (!isUndefined(this.subscriptionForGettingDrawing)) {
      this.subscriptionForGettingDrawing.unsubscribe();
    }

    this.subscriptionForGettingDrawing = this.drawings
      .getById(this.drawingId)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((drawing: Drawing) => {
        this.drawing = drawing;
        this.loadingDrawing = false;
      });
  }

  private getDrawingShapes(): void {
    if (!this.drawingShapes.length) {
      this.drawings
        .getDrawingShapes()
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((drawingShapes: string[]) => {
          this.drawingShapes = drawingShapes;
        });
    }
  }

  private setSavingAsEp(): void {
    const emptySaveAsEpState = {form: null, drawingConfigurationForEpDrawing: null} as NavigationSaveAsEpData;
    const saveAsEpState = this.navigationHelperService.getPartialState<NavigationSaveAsEpData>(Object.keys(emptySaveAsEpState));

    if (!AssertionUtils.isNullOrUndefined(saveAsEpState) && !ObjectUtils.isDeepEqual(saveAsEpState, emptySaveAsEpState)) {
      this.saveAsEpForm = saveAsEpState.form;
      this.openSaveAsEp(saveAsEpState.drawingConfigurationForEpDrawing, true);
    } else {
      this.savingAsEp = false;
    }
  }

  private getExternalApplicationLink(): Observable<string> {
    const epDrawing = this.drawing as EpDrawing;
    return this.profile
      .getExternalApplicationKey()
      .pipe(map((externalApplicationKey: string) => `txcccm://application_key:${externalApplicationKey}:${btoa(`{"cfgVersion":1, "context":"viewEp", "projectId": 0, "epId":${epDrawing.epId}}`)}`));
  }
}
